import React from 'react'
import Session from 'store/session'

import {
  Game,
  getApiPath,
  getStaticApiPath,
  getStoredValue,
  removedStoredValue
} from 'common'
import Locale from 'data/locale'

const tokenVar = 'd3p-token'

class NewApi {
  constructor(store) {
    this.store = store
    this.game = Game
  }

  async updateLoginFrontend() {
    const url = new URL(getApiPath(`users/whoami`), window.location.href)

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (result && result.userId) {
      this.store?.dispatch.setUser(
        result.userId,
        result.userName,
        result.userLevel || 1
      )
    }

    return result.userId
  }

  authHeaders() {
    const token = getStoredValue(tokenVar, () => getStoredValue(tokenVar), true)
    if (token) {
      return {
        Authorization: `Bearer ${token}`
      }
    }
  }

  signout() {
    removedStoredValue(tokenVar)
    removedStoredValue(tokenVar, true)
    if (this.store) {
      this.store.dispatch.setUser(0, '', 0)
    }
  }

  async fetchAuth(url, options) {
    const response = await fetch(url.toString(), {
      ...options,
      credentials: 'include',
      headers: { ...options.headers, ...this.authHeaders() }
    }).catch(() => null)
    if (!response) {
      return { error: 'No response.' }
    }
    if (response.status === 401) {
      this.signout()
      return { error: `Unauthorized.` }
    }
    const result = await response.json().catch(() => null)
    if (!response?.ok) {
      return { error: result?.error || `Unknown error.` }
    }
    return result
  }

  async importProfile(region, battleTag) {
    const url = new URL(getApiPath('blizzard/proxy/d3'), window.location)
    url.searchParams.append('region', region)
    url.searchParams.append('tag', battleTag.replace('#', '-'))

    const result = await fetch(url.href, {
      method: 'GET'
    })
      .then((response) => response.json())
      .catch(() => null)

    if (!result || !result.heroes) {
      const _L = Locale('ui-import.js')
      throw Error(result?.reason || result?.error || _L`Profile not found.`)
    }

    result.region = region
    return result
  }

  async importCharacter(region, battleTag, id, dead) {
    const url = new URL(getApiPath('blizzard/proxy/d3'), window.location)
    url.searchParams.append('region', region)
    url.searchParams.append('tag', battleTag.replace('#', '-'))
    url.searchParams.append('id', id)
    if (dead) url.searchParams.append('dead', '1')

    const result = await fetch(url.href, {
      method: 'GET'
    })
      .then((response) => response.json())
      .catch(() => null)

    if (!result) {
      const _L = Locale('ui-import.js')
      throw Error(result?.reason || _L`Profile not found.`)
    }

    result.region = region
    return result
  }

  async save(data, params) {
    const url = new URL(
      getStaticApiPath(`profiles/${this.game}`),
      window.location
    )
    if (params) url.search = new URLSearchParams(params).toString()

    const result = await this.fetchAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' }
    })

    if (!result || !result.id) {
      const _L = Locale('ui-import.js')
      throw Error(result?.error || _L`Failed to save profile.`)
    }

    return result.id
  }

  async load(id) {
    const result = await fetch(getApiPath(`profiles/${this.game}/${id}`), {
      method: 'GET'
    })
      .then((response) => response.json())
      .catch(() => null)

    if (!result || result.error || !result.data) {
      const _L = Locale('ui-import.js')
      throw Error(result?.error || _L`Unknown error`)
    }

    return JSON.parse(result.data)
  }

  async delete(id) {
    const result = await this.fetchAuth(getApiPath(`profiles/${id}`), {
      method: 'DELETE'
    })

    if (!result || result.code !== 'OK') {
      const _L = Locale('ui-import.js')
      throw Error(result?.error || _L`Failed to delete profile.`)
    }

    return true
  }

  async search(params) {
    const url = new URL(
      getApiPath(`search/legacy/${this.game}`),
      window.location
    )
    if (params) url.search = new URLSearchParams(params).toString()

    const result = await this.fetchAuth(url, {
      method: 'GET'
    })

    if (!result || !Array.isArray(result)) {
      const _L = Locale('ui-import.js')
      throw Error(result?.error || _L`Query failed.`)
    }

    return result.map((row) => {
      if (typeof row.date === 'string') {
        row = { ...row, date: (Date.parse(row.date) / 1000) | 0 }
      }
      return row
    })
  }

  async folders(action, params) {
    if (action === 'save') {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'POST',
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to modify folders.`)
      }
      return true
    } else if (action === 'get' || !action) {
      const result = await this.fetchAuth(getApiPath(`folders/${this.game}`), {
        method: 'GET'
      })
      if (!result || result.error) {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to load folders.`)
      }
      return result
    } else if (action === 'delall') {
      const result = await this.fetchAuth(
        getApiPath(`profiles/by-folder/${params.from}`),
        {
          method: 'DELETE'
        }
      )
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to modify folders.`)
      }
      return true
    } else if (action === 'moveall') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ from: params.from, to: params.to }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to modify folders.`)
      }
      return true
    } else if (action === 'move') {
      const result = await this.fetchAuth(getApiPath(`profiles/move`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, to: params.to }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to modify folders.`)
      }
      return true
    } else if (action === 'rename') {
      const result = await this.fetchAuth(getApiPath(`profiles/rename`), {
        method: 'POST',
        body: JSON.stringify({ id: params.id, name: params.name }),
        headers: { 'Content-Type': 'application/json' }
      })
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Failed to modify folders.`)
      }
      return true
    } else {
      throw Error('Unknown action.')
    }
  }

  async access(action, params) {
    if (!action) {
      const result = await this.fetchAuth(getApiPath(`users`), {
        method: 'GET'
      })
      if (!result || result.error) {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return result
    } else if (action === 'grant') {
      const url = new URL(
        getApiPath(`users/grant/${params.name}`),
        window.location
      )
      if (params.level) url.searchParams.append('level', params.level)
      const result = await this.fetchAuth(url.href, {
        method: 'GET'
      })
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return true
    } else if (action === 'revoke') {
      const result = await this.fetchAuth(
        getApiPath(`users/revoke/${params.id}`),
        {
          method: 'GET'
        }
      )
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return true
    } else {
      throw Error('Unknown action.')
    }
  }

  async groups(action, params) {
    if (!action) {
      const result = await this.fetchAuth(getApiPath(`users/groups`), {
        method: 'GET'
      })
      if (!result || result.error) {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return result
    } else if (action === 'assign') {
      const result = await this.fetchAuth(
        getApiPath(`users/groups/${params.id}/${params.group}`),
        {
          method: 'PATCH'
        }
      )
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return true
    } else if (action === 'delete') {
      const result = await this.fetchAuth(
        getApiPath(`users/groups/${params.id}/${params.group}`),
        {
          method: 'DELETE'
        }
      )
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return true
    } else if (action === 'create') {
      const result = await this.fetchAuth(
        getApiPath(`users/groups/${params.group}`),
        {
          method: 'POST'
        }
      )
      if (!result || result.code !== 'OK') {
        const _L = Locale('ui-import.js')
        throw Error(result?.error || _L`Error.`)
      }
      return true
    } else {
      throw Error('Unknown action.')
    }
  }
}

export const Api = NewApi

export default function useApi() {
  const store = Session.useStore()
  return React.useMemo(() => new Api(store), [store])
}
